/**
 * The Live tab's read side: the decision feed (5s poll while mounted) and
 * the frozen verdict report of the day on the clock (`at` = the DEV
 * simulated day, null = today) — keyed on both so badges follow the day on
 * screen.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useQuery } from '@tanstack/react-query';

import type { DropGenEventRow, DropGenGatewayOverview, DropGenGatewayTrends, DropGenVerdictReport } from '@trafficpulse/shared';

import { api } from '@/services/api';

import { dropGeneratorKeys } from '../useDropGenerator';

interface Envelope<T> {
  data: T;
}

const BASE = '/drop-generator';

/** The decision feed — a light 5s poll while the tab is on screen. */
export function useDgEvents(countryKey: string | null, enabled = true) {
  return useQuery({
    queryKey: dropGeneratorKeys.events(countryKey ?? 'none'),
    queryFn: async () => {
      const res = await api.get<Envelope<DropGenEventRow[]>>(
        `${BASE}/live/${countryKey}/events`,
      );
      return res.data.data;
    },
    enabled: Boolean(countryKey) && enabled,
    refetchInterval: 5_000,
    staleTime: 0,
    retry: false,
  });
}

/** The day's frozen verdict report (null before the first pass). */
export function useVerdictReport(
  countryKey: string | null,
  at: string | null = null,
  enabled = true,
) {
  return useQuery({
    queryKey: [...dropGeneratorKeys.verdicts(countryKey ?? 'none'), at],
    queryFn: async () => {
      const res = await api.get<Envelope<DropGenVerdictReport | null>>(
        `${BASE}/verdicts/${countryKey}`,
        { params: at ? { at } : undefined },
      );
      return res.data.data;
    },
    enabled: Boolean(countryKey) && enabled,
    staleTime: 30_000,
    retry: false,
  });
}

/** 19/10/2026 — the Gateways tab's read: today's rates, blocks, winners and prices (zero writes). */
export function useGatewaysOverview(countryKey: string | null, at: string | null = null) {
  return useQuery({
    queryKey: [...dropGeneratorKeys.all, 'gateways-overview', countryKey ?? 'none', at] as const,
    queryFn: async () => {
      const res = await api.get<Envelope<DropGenGatewayOverview>>(`/drop-generator/gateways/${countryKey}/today`, { params: at ? { at } : {} });
      return res.data.data;
    },
    enabled: Boolean(countryKey),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
}

/** 19/10/2026 — the Gateways tab's trends: the last N run days per gateway (zero writes). */
export function useGatewayTrends(countryKey: string | null, days: number, at: string | null = null) {
  return useQuery({
    queryKey: [...dropGeneratorKeys.all, 'gateway-trends', countryKey ?? 'none', days, at] as const,
    queryFn: async () => {
      const res = await api.get<Envelope<DropGenGatewayTrends>>(`/drop-generator/gateways/${countryKey}/trends`, { params: { days, ...(at ? { at } : {}) } });
      return res.data.data;
    },
    enabled: Boolean(countryKey),
    staleTime: 60_000,
  });
}
